import { Box } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../context/ThemeContext";
import { paddedDomain } from "../../shared/chartMath";
import { Tone } from "../general/dataAtoms/shared.ts";
import { toneZoneColors, ZoneColor } from "./scatter/ZonedScatter.tsx";

/** The geometry of the strip, in px: its line runs through the band. */
const HEIGHT = 38;
const LINE_Y = 9;
const BAND_HEIGHT = 12;
const MIDPOINT_HEIGHT = 18;
const MARK_SIZE = 12;
const LABEL_Y = 22;

/**
 * The band of the range is a light shade of the primary colour, and
 * see-through: the line of the strip runs on behind it.
 */
const BAND_LIGHTEN = 0.6;
const BAND_ALPHA = 0.45;

/**
 * The dot of a settlement where it is not on its chart: on the strip of its
 * range, and beside the figure it stands for. Filled and edged as on the chart.
 */
export const SettlementMark = ({ color }: { color: ZoneColor }) => (
  <Box
    component="span"
    sx={{
      flex: "none",
      display: "block",
      width: MARK_SIZE,
      height: MARK_SIZE,
      borderRadius: "50%",
      bgcolor: color.dot,
      border: `1px solid ${color.edge}`,
    }}
  />
);

/**
 * One settlement against the bargaining range at its stage, as a strip: the
 * band from the client's EV to the end of the opponent's bargaining range (the
 * opponent's EV, negated), a tick at its midpoint (the even split), and the
 * dot of the settlement at the settled figure, in the colour of the zone it is
 * in on the chart, with the band named underneath. The strip always holds the
 * settlement, so a deal struck well outside the range reads honestly as a dot
 * far from the band.
 *
 * Drawn for a settlement that has a dot, and so a range to be placed in.
 */
export const SettlementRangeStrip = ({
  partyEv,
  opponentRangeEnd,
  mid,
  settledFigure,
  tone,
}: {
  partyEv: number;
  opponentRangeEnd: number;
  mid: number;
  settledFigure: number;
  // the zone of the settlement's dot, which its dot here takes its colour from
  tone: Tone;
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "portfolio.settlement.quality",
  });

  // padded, so a dot at an end never sits on the edge
  const { min: domainMin, max: domainMax } = paddedDomain([
    partyEv,
    opponentRangeEnd,
    settledFigure,
  ]);
  // where an amount stands along the strip, in % from its start
  const at = (v: number) =>
    ((v - domainMin) / (domainMax - domainMin || 1)) * 100;

  // partyEv is the client floor (lower); opponentRangeEnd the ceiling (higher)
  const bandLeft = at(Math.min(partyEv, opponentRangeEnd));
  const bandRight = at(Math.max(partyEv, opponentRangeEnd));
  const bandMid = (bandLeft + bandRight) / 2;

  return (
    <Box
      role="img"
      aria-label={t("stripLabel")}
      sx={{ position: "relative", height: HEIGHT, mt: 1.125 }}
    >
      <Box
        sx={{
          position: "absolute",
          left: 0,
          right: 0,
          top: LINE_Y,
          borderTop: 1,
          borderColor: "divider",
        }}
      />
      {/* the range: client's EV → end of the opponent's bargaining range */}
      <Box
        sx={{
          position: "absolute",
          left: `${bandLeft}%`,
          width: `${bandRight - bandLeft}%`,
          minWidth: "1px",
          top: LINE_Y - BAND_HEIGHT / 2,
          height: BAND_HEIGHT,
          borderRadius: 0.75,
          bgcolor: alpha(
            lighten(theme.palette.primary.main, BAND_LIGHTEN),
            BAND_ALPHA,
          ),
        }}
      />
      {/* its midpoint: the even split */}
      <Box
        sx={{
          position: "absolute",
          left: `${at(mid)}%`,
          top: LINE_Y - MIDPOINT_HEIGHT / 2,
          height: MIDPOINT_HEIGHT,
          borderLeft: `1.5px solid ${theme.palette.text.primary}`,
        }}
      />
      {/* what was agreed */}
      <Box
        sx={{
          position: "absolute",
          left: `${at(settledFigure)}%`,
          top: LINE_Y,
          transform: "translate(-50%, -50%)",
        }}
      >
        <SettlementMark color={toneZoneColors(theme)[tone]} />
      </Box>
      {/* The name of the band, centred under it. Its box reaches from the
          nearer end of the strip to as far beyond the middle of the band, and
          is no narrower than the name: under a band near an end the name
          moves in only as far as it takes to stay whole inside the strip. */}
      <Box
        sx={{
          position: "absolute",
          top: LABEL_Y,
          ...(bandMid <= 50
            ? { left: 0, width: `${2 * bandMid}%` }
            : { right: 0, width: `${2 * (100 - bandMid)}%` }),
          minWidth: "max-content",
          textAlign: "center",
          typography: "caption",
          lineHeight: 1.3,
          color: "text.secondary",
        }}
      >
        {t("stripRange")}
      </Box>
    </Box>
  );
};
